// Régua de prazos do módulo de agrupamento de faturamento (#5787, projeto 9).
//
// O contrato da Prefeitura tem calendário próprio, diferente do resto do sistema:
//   - faturar e emitir até o ÚLTIMO DIA ÚTIL do mês seguinte à competência;
//   - auditar no 1º DIA ÚTIL do mês seguinte à emissão;
//   - boleto vence no dia combinado no agrupamento (15 por padrão);
//   - malote com a NF até o 5º DIA ÚTIL, e é dessa entrega que corre o prazo de 30 dias do edital.
//
// ⚠️ Isto NÃO substitui src/lib/janelaFaturamento.ts no resto do sistema. A régua individual
// (vencimento da distribuidora − 7 dias) continua valendo para UC fora de agrupamento. Para UC
// dentro de um agrupamento ativo ela dispara atraso falso: em AGO/2026, 114 das 117 faturas da
// Prefeitura vencem em 30/09, então o limite individual (23/09) marcaria como atrasada justamente
// a emissão feita no prazo do contrato. Quem decide o atraso dessas UCs é esta régua.
//
// Sem I/O e sem Date.now(): a data de hoje entra como parâmetro, para o teste ser determinístico.

/** Data em ISO curto, sempre "YYYY-MM-DD". O sistema inteiro troca datas nesse formato. */
export type DataIso = string;

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date): DataIso => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
/** Sempre em UTC: com fuso local, 01/10 00:00 em Goiás vira 30/09 e a régua erra por um dia. */
const emUtc = (ano: number, mes: number, dia: number) => new Date(Date.UTC(ano, mes - 1, dia));
/**
 * Converte "YYYY-MM-DD" (aceita timestamp, usa só a data) em Date UTC.
 *
 * Valida na entrada e lança: sem isso, uma data inválida vira NaN e os laços de dia útil deste
 * arquivo nunca terminam — a aba do navegador congela em vez de mostrar erro.
 */
const doIso = (s: DataIso) => {
  const texto = String(s ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) {
    throw new Error(`Data inválida na régua de prazos: ${JSON.stringify(s)}. Esperado "YYYY-MM-DD".`);
  }
  const [a, m, d] = texto.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) {
    throw new Error(`Data inválida na régua de prazos: ${texto}.`);
  }
  const data = emUtc(a, m, d);
  if (isNaN(data.getTime())) throw new Error(`Data inválida na régua de prazos: ${texto}.`);
  return data;
};
const somaDias = (s: DataIso, dias: number) => {
  const d = doIso(s);
  d.setUTCDate(d.getUTCDate() + dias);
  return iso(d);
};
/** Primeiro dia do mês seguinte ao da data informada. */
const proximoMes = (s: DataIso) => {
  const d = doIso(s);
  return iso(emUtc(d.getUTCFullYear(), d.getUTCMonth() + 2, 1));
};

/**
 * Domingo de Páscoa (algoritmo de Meeus/Butcher). É dele que saem Carnaval, Sexta-feira Santa
 * e Corpus Christi, que mudam de data todo ano.
 */
export function domingoDePascoa(ano: number): DataIso {
  const a = ano % 19;
  const b = Math.floor(ano / 100);
  const c = ano % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31);
  const dia = ((h + l - 7 * m + 114) % 31) + 1;
  return iso(emUtc(ano, mes, dia));
}

/** Feriados fixos: nacionais, mais o estadual de Goiás e o municipal de Morrinhos. */
const FIXOS: { mes: number; dia: number; nome: string }[] = [
  { mes: 1, dia: 1, nome: 'Confraternização Universal' },
  { mes: 4, dia: 21, nome: 'Tiradentes' },
  { mes: 5, dia: 1, nome: 'Dia do Trabalho' },
  { mes: 9, dia: 7, nome: 'Independência' },
  { mes: 10, dia: 12, nome: 'Nossa Senhora Aparecida' },
  { mes: 11, dia: 2, nome: 'Finados' },
  { mes: 11, dia: 15, nome: 'Proclamação da República' },
  { mes: 11, dia: 20, nome: 'Consciência Negra' },
  { mes: 12, dia: 25, nome: 'Natal' },
  // Estadual de Goiás.
  { mes: 10, dia: 24, nome: 'Fundação de Goiás (GO)' },
  // Municipal de Morrinhos — confirmar com a Prefeitura antes do primeiro ciclo real.
  { mes: 8, dia: 15, nome: 'Aniversário de Morrinhos (a confirmar)' },
];

export interface Feriado {
  data: DataIso;
  nome: string;
}

/** Feriados do ano, fixos e móveis. Ponto facultativo não entra: não é feriado. */
export function feriadosDoAno(ano: number): Feriado[] {
  const pascoa = domingoDePascoa(ano);
  const moveis: Feriado[] = [
    { data: somaDias(pascoa, -47), nome: 'Carnaval' }, // terça-feira de Carnaval
    { data: somaDias(pascoa, -2), nome: 'Sexta-feira Santa' },
    { data: somaDias(pascoa, 60), nome: 'Corpus Christi' },
  ];
  const fixos = FIXOS.map(f => ({ data: iso(emUtc(ano, f.mes, f.dia)), nome: f.nome }));
  return [...fixos, ...moveis].sort((a, b) => a.data.localeCompare(b.data));
}

const cacheFeriados = new Map<number, Set<string>>();
function feriadosSet(ano: number): Set<string> {
  let s = cacheFeriados.get(ano);
  if (!s) {
    s = new Set(feriadosDoAno(ano).map(f => f.data));
    cacheFeriados.set(ano, s);
  }
  return s;
}

/**
 * Cadastro que o time mantém na tela (tabela `feriados`): acrescenta feriado que o cálculo não
 * conhece e suprime um padrão que não vale (o municipal de Morrinhos entrou "a confirmar").
 * Vazio = comportamento original.
 */
export interface AjustesFeriados {
  /** Datas que SÃO feriado, além das calculadas. */
  incluir?: Iterable<DataIso>;
  /** Datas que NÃO são feriado, mesmo que o cálculo diga que sim. */
  excluir?: Iterable<DataIso>;
}

const soData = (d: DataIso) => String(d ?? '').slice(0, 10);

export function ehFeriado(data: DataIso, ajustes?: AjustesFeriados): boolean {
  const dia = soData(data);
  // `excluir` ganha de tudo: é a forma de apagar um feriado que o código chutou errado.
  if (ajustes?.excluir && [...ajustes.excluir].some(d => soData(d) === dia)) return false;
  if (ajustes?.incluir && [...ajustes.incluir].some(d => soData(d) === dia)) return true;
  return feriadosSet(Number(dia.slice(0, 4))).has(dia);
}

export function ehDiaUtil(data: DataIso, ajustes?: AjustesFeriados): boolean {
  const dia = doIso(data).getUTCDay();
  return dia !== 0 && dia !== 6 && !ehFeriado(data, ajustes);
}

/** Último dia útil do mês da data informada. */
export function ultimoDiaUtilDoMes(anoMes: DataIso, ajustes?: AjustesFeriados): DataIso {
  const d = doIso(anoMes);
  // Dia 0 do mês seguinte é o último dia deste mês.
  let atual = iso(emUtc(d.getUTCFullYear(), d.getUTCMonth() + 2, 0));
  while (!ehDiaUtil(atual, ajustes)) atual = somaDias(atual, -1);
  return atual;
}

/**
 * Dia fixo do mês (regra própria da Secretaria, ex.: "emitir até o dia 25"). Se o dia não existir
 * no mês, vale o último; se cair em dia não útil, ANTECIPA — o prazo não pode escorregar para
 * depois do combinado.
 */
export function diaFixoUtilDoMes(anoMes: DataIso, dia: number, ajustes?: AjustesFeriados): DataIso {
  const d = doIso(anoMes);
  const ultimoDoMes = Number(iso(emUtc(d.getUTCFullYear(), d.getUTCMonth() + 2, 0)).slice(8, 10));
  const alvo = Math.min(Math.max(Math.trunc(dia) || 1, 1), ultimoDoMes);
  let atual = `${anoMes.slice(0, 8)}${pad(alvo)}`;
  while (!ehDiaUtil(atual, ajustes)) atual = somaDias(atual, -1);
  return atual;
}

/** Primeiro dia útil do mês da data informada. */
export function primeiroDiaUtilDoMes(anoMes: DataIso, ajustes?: AjustesFeriados): DataIso {
  return nDiaUtilDoMes(anoMes, 1, ajustes);
}

/** N-ésimo dia útil do mês (1 = primeiro). */
export function nDiaUtilDoMes(anoMes: DataIso, n: number, ajustes?: AjustesFeriados): DataIso {
  if (!Number.isInteger(n) || n < 1) throw new Error('O n-ésimo dia útil começa em 1.');
  const d = doIso(anoMes);
  let atual = iso(emUtc(d.getUTCFullYear(), d.getUTCMonth() + 1, 1));
  let contados = ehDiaUtil(atual, ajustes) ? 1 : 0;
  while (contados < n) {
    atual = somaDias(atual, 1);
    if (ehDiaUtil(atual, ajustes)) contados++;
  }
  return atual;
}

/** Dias úteis entre duas datas (positivo = `ate` está no futuro). Não conta o dia de partida. */
export function diasUteisEntre(de: DataIso, ate: DataIso, ajustes?: AjustesFeriados): number {
  const inicio = soData(de);
  const fim = soData(ate);
  doIso(inicio); doIso(fim); // valida os dois antes de entrar no laço
  if (inicio === fim) return 0;
  const frente = inicio < fim;
  let atual = inicio;
  let n = 0;
  while (atual !== fim) {
    atual = somaDias(atual, frente ? 1 : -1);
    if (ehDiaUtil(atual, ajustes)) n += frente ? 1 : -1;
  }
  return n;
}

/** Quantos dias úteis antes do limite a tela começa a alertar (decisão de 16/09/2026). */
export const DIAS_UTEIS_ALERTA_EMISSAO = 3;

export type EstadoPrazoEmissao =
  | 'no_prazo'
  | 'atencao'
  | 'vencido'
  | 'emitido_no_prazo'
  | 'emitido_fora_do_prazo';

export const ROTULO_PRAZO: Record<EstadoPrazoEmissao, string> = {
  no_prazo: 'No prazo',
  atencao: 'Prazo perto de estourar',
  vencido: 'Prazo de emissão estourado',
  emitido_no_prazo: 'Emitido no prazo',
  emitido_fora_do_prazo: 'Emitido fora do prazo',
};

/** Regra própria da Secretaria. NULL/ausente em qualquer campo = padrão do contrato. */
export interface RegraAgrupamento {
  /** Dia do mês em que a emissão vence. Ausente = último dia útil. */
  diaLimiteEmissao?: number | null;
  /** N-ésimo dia útil do mês seguinte para a auditoria. Ausente = 1º. */
  diaAuditoriaUtil?: number | null;
  /** N-ésimo dia útil do mês seguinte para o malote. Ausente = 5º. */
  diaMaloteUtil?: number | null;
  /** Dias corridos após o malote até o limite de pagamento. Ausente = 30. */
  diasPagamentoEdital?: number | null;
}

/** Datas escritas à mão para UM mês. Cada uma substitui a calculada. */
export interface DatasAjustadas {
  limiteEmissao?: DataIso | null;
  auditoria?: DataIso | null;
  vencimento?: DataIso | null;
  malote?: DataIso | null;
}

export interface ReguaPrazos {
  /** Competência do fechamento, "YYYY-MM-01". */
  competencia: DataIso;
  /** Último dia útil do mês seguinte à competência: limite para faturar e emitir. */
  limiteEmissao: DataIso;
  /** 1º dia útil do mês seguinte ao da emissão. */
  auditoria: DataIso;
  /** Vencimento do boleto, no dia do agrupamento (adiado se cair em dia não útil). */
  vencimento: DataIso;
  /** 5º dia útil do mês seguinte ao da emissão: entrega do malote com a NF. */
  malote: DataIso;
  /** Data-limite de pagamento pelo edital: 30 dias corridos após a entrega do malote. */
  limitePagamentoEdital: DataIso;
  estado: EstadoPrazoEmissao;
  /** Dias úteis até o limite (negativo = já passou). null quando já emitiu. */
  diasUteisAteLimite: number | null;
  /** Quais das 4 datas vieram de ajuste manual, para a tela marcar "ajustada". */
  ajustadas: { limiteEmissao: boolean; auditoria: boolean; vencimento: boolean; malote: boolean };
}

export interface EntradaRegua {
  /** Competência do fechamento ("2026-08-01" = AGO/2026). */
  competencia: DataIso;
  /** Dia de vencimento do agrupamento (1 a 28). */
  diaVencimento: number;
  hojeIso: DataIso;
  /** Data em que o boleto do agrupamento foi emitido, se já saiu. */
  emitidoEm?: DataIso | null;
  /** Cadastro de feriados da tela (tabela `feriados`). */
  feriados?: AjustesFeriados;
  /** Regra própria da Secretaria. */
  regra?: RegraAgrupamento | null;
  /** Datas escritas à mão para este mês. */
  ajustes?: DatasAjustadas | null;
}

/**
 * Monta a régua da competência. O mês de trabalho é o seguinte à competência: AGO/2026 fecha e
 * emite até o último dia útil de setembro, audita no 1º dia útil de outubro e entrega o malote
 * até o 5º dia útil de outubro.
 */
export function montarRegua({
  competencia, diaVencimento, hojeIso, emitidoEm, feriados, regra, ajustes,
}: EntradaRegua): ReguaPrazos {
  const mesCompetencia = soData(competencia).slice(0, 8) + '01';
  doIso(mesCompetencia);
  const mesTrabalho = proximoMes(mesCompetencia);
  const mesSeguinte = proximoMes(mesTrabalho);

  const ajustada = (v: DataIso | null | undefined) => {
    const d = v ? soData(v) : '';
    if (!d) return null;
    doIso(d); // ajuste inválido estoura aqui, não no meio de um laço
    return d;
  };
  const ajusteLimite = ajustada(ajustes?.limiteEmissao);
  const ajusteAuditoria = ajustada(ajustes?.auditoria);
  const ajusteVencimento = ajustada(ajustes?.vencimento);
  const ajusteMalote = ajustada(ajustes?.malote);

  // Regra própria da Secretaria quando existir; senão o padrão do contrato.
  const limiteEmissao = ajusteLimite ?? (regra?.diaLimiteEmissao
    ? diaFixoUtilDoMes(mesTrabalho, regra.diaLimiteEmissao, feriados)
    : ultimoDiaUtilDoMes(mesTrabalho, feriados));
  const auditoria = ajusteAuditoria ?? nDiaUtilDoMes(mesSeguinte, regra?.diaAuditoriaUtil || 1, feriados);
  const malote = ajusteMalote ?? nDiaUtilDoMes(mesSeguinte, regra?.diaMaloteUtil || 5, feriados);

  let vencimento = ajusteVencimento;
  if (!vencimento) {
    const dia = Math.min(Math.max(Math.trunc(diaVencimento) || 15, 1), 28);
    vencimento = mesSeguinte.slice(0, 8) + pad(dia);
    // Vencimento em dia não útil adia: o banco não registra baixa no feriado.
    while (!ehDiaUtil(vencimento, feriados)) vencimento = somaDias(vencimento, 1);
  }

  const emitido = emitidoEm ? soData(emitidoEm) : null;
  const diasUteisAteLimite = emitido ? null : diasUteisEntre(soData(hojeIso), limiteEmissao, feriados);

  const estado: EstadoPrazoEmissao = emitido
    ? (emitido <= limiteEmissao ? 'emitido_no_prazo' : 'emitido_fora_do_prazo')
    : soData(hojeIso) > limiteEmissao
      ? 'vencido'
      : (diasUteisAteLimite ?? 0) <= DIAS_UTEIS_ALERTA_EMISSAO
        ? 'atencao'
        : 'no_prazo';

  return {
    competencia: mesCompetencia,
    limiteEmissao,
    auditoria,
    vencimento,
    malote,
    limitePagamentoEdital: somaDias(malote, regra?.diasPagamentoEdital || 30),
    estado,
    diasUteisAteLimite,
    ajustadas: {
      limiteEmissao: !!ajusteLimite,
      auditoria: !!ajusteAuditoria,
      vencimento: !!ajusteVencimento,
      malote: !!ajusteMalote,
    },
  };
}

/** Frase curta para o cartão da tela. */
export function frasePrazo(r: ReguaPrazos): string {
  const br = (d: DataIso) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  switch (r.estado) {
    case 'emitido_no_prazo': return `Emitido dentro do prazo (limite era ${br(r.limiteEmissao)}).`;
    case 'emitido_fora_do_prazo': return `Emitido depois do limite de ${br(r.limiteEmissao)}.`;
    case 'vencido': return `O prazo de emissão era ${br(r.limiteEmissao)} e já passou.`;
    case 'atencao': return r.diasUteisAteLimite === 0
      ? `Último dia para emitir é hoje (${br(r.limiteEmissao)}).`
      : `Faltam ${r.diasUteisAteLimite} dia(s) útil(eis) para o limite de ${br(r.limiteEmissao)}.`;
    default: return `No prazo: emitir até ${br(r.limiteEmissao)}.`;
  }
}

// ─── Malote (#5788) ─────────────────────────────────────────────────────────────────────────
// O malote leva a fatura, o relatório por UC e o demonstrativo (item 9.2 do contrato). Não existe
// Nota Fiscal: a demanda 03 (#5779) confirmou que, por ser locação, sai só a fatura.
//
// O prazo de pagamento conta do LIMITE de entrega (o 5º dia útil da régua), não da data em que
// alguém registrou a entrega — o contrato fixa o limite, não um protocolo. Registrar serve de prova
// e para saber quem ainda não entregou.

export type EstadoMalote = 'entregue_no_prazo' | 'entregue_fora_do_prazo' | 'pendente' | 'atrasado';

export const ROTULO_MALOTE: Record<EstadoMalote, string> = {
  entregue_no_prazo: 'Malote entregue',
  entregue_fora_do_prazo: 'Entregue após o limite',
  pendente: 'Malote a entregar',
  atrasado: 'Malote atrasado',
};

export interface SituacaoMalote {
  estado: EstadoMalote;
  /** Limite de entrega da régua, "YYYY-MM-DD". */
  limite: DataIso;
  /** Data-limite de pagamento pelo edital, contada do limite de entrega. */
  limitePagamento: DataIso;
  frase: string;
}

export function situacaoMalote(
  r: ReguaPrazos,
  entregueEm: DataIso | null | undefined,
  hojeIso: DataIso,
): SituacaoMalote {
  const br = (d: DataIso) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
  const entregue = entregueEm ? soData(entregueEm) : null;
  const hoje = soData(hojeIso);
  const estado: EstadoMalote = entregue
    ? (entregue <= r.malote ? 'entregue_no_prazo' : 'entregue_fora_do_prazo')
    : (hoje > r.malote ? 'atrasado' : 'pendente');
  const frase = estado === 'entregue_no_prazo'
    ? `Entregue em ${br(entregue!)}, dentro do limite de ${br(r.malote)}.`
    : estado === 'entregue_fora_do_prazo'
      ? `Entregue em ${br(entregue!)}, depois do limite de ${br(r.malote)}.`
      : estado === 'atrasado'
        ? `O limite de entrega era ${br(r.malote)} e já passou.`
        : `Entregar até ${br(r.malote)}.`;
  return { estado, limite: r.malote, limitePagamento: r.limitePagamentoEdital, frase };
}
